import React from 'react';
import { Model } from '@/types/model';

// Import SVG logos
import { ReactComponent as QwenLogo } from '@/assets/v2/models/qwen.svg';
import { ReactComponent as QwenFlatLogo } from '@/assets/v2/models/qwen-flat.svg';
import { ReactComponent as MetaLogo } from '@/assets/v2/models/meta.svg';
import { ReactComponent as MetaFlatLogo } from '@/assets/v2/models/meta-flat.svg';
import { ReactComponent as MistralLogo } from '@/assets/v2/models/mistral.svg';
import { ReactComponent as MistralFlatLogo } from '@/assets/v2/models/mistral-flat.svg';
import { ReactComponent as GoogleLogo } from '@/assets/v2/models/google.svg';
import { ReactComponent as GoogleFlatLogo } from '@/assets/v2/models/google-flat.svg';
import { ReactComponent as MicrosoftLogo } from '@/assets/v2/models/microsoft.svg';
import { ReactComponent as MicrosoftFlatLogo } from '@/assets/v2/models/microsoft-flat.svg';
import { ReactComponent as UpstageLogo } from '@/assets/v2/models/upstage.svg';
import { ReactComponent as UpstageFlatLogo } from '@/assets/v2/models/upstage-flat.svg';
import { ReactComponent as HuggingFaceLogo } from '@/assets/v2/models/huggingface.svg';
import { ReactComponent as HuggingFaceFlatLogo } from '@/assets/v2/models/huggingface-flat.svg';
import { ReactComponent as AllHandsLogo } from '@/assets/v2/models/allhands.svg';
import { ReactComponent as AllHandsFlatLogo } from '@/assets/v2/models/allhands-flat.svg';
import { ReactComponent as DeepseekLogo } from '@/assets/v2/models/deepseek.svg';
import { ReactComponent as DeepseekFlatLogo } from '@/assets/v2/models/deepseek-flat.svg';

// Logo mapping based on model family/name
const logoMap = {
    // Deepseek models
    'deepseek': { logo: DeepseekLogo, flatLogo: DeepseekFlatLogo },

    // Qwen models
    'qwen': { logo: QwenLogo, flatLogo: QwenFlatLogo },

    // Meta models
    'llama': { logo: MetaLogo, flatLogo: MetaFlatLogo },
    'codellama': { logo: MetaLogo, flatLogo: MetaFlatLogo },
    'meta': { logo: MetaLogo, flatLogo: MetaFlatLogo },

    // Mistral models
    'mistral': { logo: MistralLogo, flatLogo: MistralFlatLogo },
    'mistral-nemo': { logo: MistralLogo, flatLogo: MistralFlatLogo },
    'mixtral': { logo: MistralLogo, flatLogo: MistralFlatLogo },

    // Google models
    'gemma': { logo: GoogleLogo, flatLogo: GoogleFlatLogo },
    'google': { logo: GoogleLogo, flatLogo: GoogleFlatLogo },

    // Microsoft models
    'phi': { logo: MicrosoftLogo, flatLogo: MicrosoftFlatLogo },
    'microsoft': { logo: MicrosoftLogo, flatLogo: MicrosoftFlatLogo },

    // Upstage models
    'solar': { logo: UpstageLogo, flatLogo: UpstageFlatLogo },
    'upstage': { logo: UpstageLogo, flatLogo: UpstageFlatLogo },

    // Hugging Face models
    'zephyr': { logo: HuggingFaceLogo, flatLogo: HuggingFaceFlatLogo },
    'huggingface': { logo: HuggingFaceLogo, flatLogo: HuggingFaceFlatLogo },

    // All Hands models
    'allhands': { logo: AllHandsLogo, flatLogo: AllHandsFlatLogo },
    'all-hands': { logo: AllHandsLogo, flatLogo: AllHandsFlatLogo },
    'all hands': { logo: AllHandsLogo, flatLogo: AllHandsFlatLogo },
};

export type LogoType = 'logo' | 'flat';

interface ModelLogoProps {
    model: Model | null | undefined;
    type: LogoType;
    className?: string;
    viewBox?: string;
    style?: React.CSSProperties;
}

/**
 * Gets the appropriate logo component for a given model
 * @param model - The model object
 * @param type - The type of logo ('logo' or 'flat')
 * @param className - Optional CSS class name
 * @returns React component for the logo
 */
export const getModelLogo = (
    { model, type = 'logo', className, viewBox, style }: ModelLogoProps
): React.ReactNode => {
    // Try to match by family first, then by displayName, then by modelShortString
    const searchTerms = [
        model.family?.toLowerCase(),
        model.displayName?.toLowerCase(),
        model.modelShortString?.toLowerCase(),
    ].filter(Boolean);

    // Create props object for the SVG component
    const svgProps: any = { className };
    if (viewBox !== undefined) svgProps.viewBox = viewBox;
    if (style !== undefined) svgProps.style = style;

    for (const term of searchTerms) {
        if (term) {
            // Check for exact match first
            if (logoMap[term]) {
                const LogoComponent = type === 'flat' ? logoMap[term].flatLogo : logoMap[term].logo;
                return <LogoComponent {...svgProps} />;
            }

            // Check for partial matches
            for (const [key, value] of Object.entries(logoMap)) {
                if (term.includes(key) || key.includes(term)) {
                    const LogoComponent = type === 'flat' ? value.flatLogo : value.logo;
                    return <LogoComponent {...svgProps} />;
                }
            }
        }
    }

    return null;
};
